import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button, Dialog, Flex } from '@/components/common';

interface ArchiveConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerName?: string;
  onConfirm: () => void | Promise<void>;
}

/**
 * Archiving hides the thread from your inbox until the next message. The other
 * person keeps it, and either side can send again at any time.
 */
export function ArchiveConversationDialog({
  open,
  onOpenChange,
  partnerName,
  onConfirm,
}: ArchiveConversationDialogProps) {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const [archiving, setArchiving] = useState(false);

  const handleConfirm = async () => {
    setArchiving(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to archive conversation:', err);
      toast.error(t('inbox.archiveError'));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="1" maxWidth="320px">
        <Flex direction="column" gap="3" py="2">
          <Dialog.Title align="center" size="3" weight="medium" mb="0">
            {t('inbox.archiveConfirm.title')}
          </Dialog.Title>
          <Dialog.Description align="center" size="1" color="gray" mb="0">
            {t('inbox.archiveConfirm.body', {
              name: partnerName ?? t('inbox.archiveConfirm.partnerFallback'),
            })}
          </Dialog.Description>
          <Flex direction="column" gap="2" width="100%" mt="2">
            <Button
              type="button"
              color="crimson"
              highContrast
              fullWidth
              disabled={archiving}
              isLoading={archiving}
              onClick={() => void handleConfirm()}
              data-testid="archive-conversation-confirm"
            >
              {t('inbox.archiveConfirm.confirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              color="gray"
              fullWidth
              disabled={archiving}
              onClick={() => onOpenChange(false)}
            >
              {tCommon('actions.cancel')}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
